import { getNodes } from '@/utils/graphqlUtils';

import { BdgResolverEntityInput } from './bdgTriggerInfo';
import {
  DispositionsDiagram_DispositionScenarioTerminalFragment,
  DispositionsDiagram_EntityFragment,
} from './graphql/DispositionsDiagram.generated';

/**
 * Walks every depth-1 entity recipient reachable from the root query:
 * root scenarios (both directions), root BDG tiers, and (by default)
 * possiblePrimaryClients scenarios. Each recipient's subtype carries its
 * own scenarios + BDG tiers inline (depth-2 cap), so no second fetch is
 * needed to resolve the cascade.
 *
 * Pass `firstGrantorDeathId` to collect only the active death order.
 * Without it, both root and primary-client death orders are collected.
 *
 * Pass `includePrimaryClientScenarios: false` when re-rooting at a pour-over
 * look-through target: the household's primary-client scenarios are already
 * resolved at the root level, so re-collecting them here would only redundantly
 * re-key those recipients (which the merge then spreads over the root entries).
 */
export function collectDownstreamRecipients(
  rootEntity: DispositionsDiagram_EntityFragment,
  {
    includePrimaryClientScenarios = true,
    firstGrantorDeathId,
  }: {
    includePrimaryClientScenarios?: boolean;
    firstGrantorDeathId?: string;
  } = {}
): Map<string, BdgResolverEntityInput> {
  const map = new Map<string, BdgResolverEntityInput>();
  type RecipientLike = {
    id?: string | null;
    subtype?: { id?: string | null } | null;
  };
  const addRecipient = (entity: RecipientLike | null | undefined): void => {
    if (!entity?.id || !entity.subtype) return;
    if (entity.id === rootEntity.id) return;
    // Depth-1 recipient subtypes use Terminal-flavored fragments, which
    // are nominally distinct from the root subtype but structurally cover
    // everything the resolver reads. The cast is scoped to the narrow
    // `BdgResolverEntityInput` contract — not the full entity fragment.
    map.set(entity.id, entity as unknown as BdgResolverEntityInput);
  };
  // Testamentary-entity and individual recipients expose `dispositionScenarios`
  // at the top level rather than under `subtype`. Wrap them so the shared
  // resolver — which reads `subtype.dispositionScenarios` — can resolve them
  // alongside entity recipients. Neither is ever a BDG entity, so the
  // resolver's non-BDG branch handles them. Without this, a pour-over target
  // that distributes to a testamentary trust (e.g. a family trust formed at the
  // first death) loses every recipient one level below that trust.
  type ScenarioCarrierLike = {
    id?: string | null;
    dispositionScenarios?:
      | DispositionsDiagram_DispositionScenarioTerminalFragment[]
      | null;
  };
  const addScenarioCarrier = (
    recipient: ScenarioCarrierLike | null | undefined
  ): void => {
    if (!recipient?.id || !recipient.dispositionScenarios) return;
    if (recipient.id === rootEntity.id) return;
    map.set(recipient.id, {
      id: recipient.id,
      subtype: {
        dispositionScenarios: recipient.dispositionScenarios,
      },
    } as unknown as BdgResolverEntityInput);
  };
  type DpLike = {
    entity?: RecipientLike | null;
    testamentaryEntity?: ScenarioCarrierLike | null;
    individual?: ScenarioCarrierLike | null;
  };
  type DpConnection = {
    edges?: ({ node?: DpLike | null } | null)[] | null;
  };
  type ScenarioLike = {
    firstGrantorDeath?: { id?: string | null } | null;
    dispositiveProvisions?: DpConnection | null;
    dispositiveProvisionsTemplate?: {
      dispositiveProvisions?: DpConnection | null;
    } | null;
    secondDeathDispositiveProvisions?: DpConnection | null;
    secondDeathDispositiveProvisionsTemplate?: {
      dispositiveProvisions?: DpConnection | null;
    } | null;
  };
  const addDps = (dps: readonly DpLike[]): void => {
    dps.forEach((dp) => {
      addRecipient(dp.entity);
      addScenarioCarrier(dp.testamentaryEntity);
      addScenarioCarrier(dp.individual);
    });
  };
  const addScenarios = (
    scenarios: readonly ScenarioLike[] | null | undefined
  ): void => {
    scenarios?.forEach((s) => {
      if (
        firstGrantorDeathId &&
        s.firstGrantorDeath?.id !== firstGrantorDeathId
      )
        return;
      addDps(
        getNodes(
          s.dispositiveProvisionsTemplate?.dispositiveProvisions ??
            s.dispositiveProvisions
        )
      );
      addDps(
        getNodes(
          s.secondDeathDispositiveProvisionsTemplate?.dispositiveProvisions ??
            s.secondDeathDispositiveProvisions
        )
      );
    });
  };

  addScenarios(rootEntity.subtype?.dispositionScenarios);
  if (includePrimaryClientScenarios) {
    rootEntity.household?.possiblePrimaryClients?.forEach((c) =>
      addScenarios(c.dispositionScenarios)
    );
  }

  const subtype = rootEntity.subtype;
  if (subtype && 'beneficiaryDesignations' in subtype) {
    const bdgs = subtype.beneficiaryDesignations;
    [bdgs?.primary, bdgs?.contingent].forEach((tier) => {
      addDps(getNodes(tier?.dispositiveProvisions));
    });
  }

  return map;
}
